/* eslint-disable */
import React, { useMemo } from 'react';
import type { FC } from 'react';
import { Grid, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { isEmpty } from 'lodash';
import { FormRenderProps } from 'react-final-form';
import { useAuth } from 'src/hooks/use-auth';
import {
  FieldConditionActionTypes,
  FieldConditionsI,
  FieldDetailI,
  FieldDetails,
  RootFieldDetailsI,
} from 'src/interfaces/application.interfaces';
import { validateRules } from 'src/pages/application/applicant-validation-creator';

import { convertSectionToLabel } from '../application-logic';
import DynamicField from './dynamic-field';
import useE2eStore from 'src/e2e-store';
import { MEDICAL_INDUSTRY_IDS } from 'src/utils/bread-e2e-constants';

interface DynamicSectionsI {
  containsBread: Boolean;
  selectedFormDetails: FieldDetailI[];
  fieldDetailType: FieldDetails;
  formProps: FormRenderProps;
  formikProps?: FormikProps<any>;
  FieldComponent?: any;
  required_fields?: string[];
  // modified line
  registerFieldRef?: (fieldId: string, element: HTMLElement | null) => void;
  handleFieldBlur?: (fieldId: string) => void;
  // end modified
}

const DynamicSections: FC<DynamicSectionsI | any> = (props: DynamicSectionsI) => {
  // modified line - Added registerFieldRef and handleFieldBlur
  const {
    containsBread,
    selectedFormDetails,
    fieldDetailType,
    formProps,
    formikProps,
    FieldComponent,
    required_fields,
    registerFieldRef,
    handleFieldBlur,
  } = props;
  const { user } = useAuth();
  const industry_id = useE2eStore((state) => state.store_details?.industry_id);
  const getSectionList = () => {
    const b: Record<string, FieldDetailI[]> = {};
    selectedFormDetails?.forEach((s) => {
      const section = s.section;

      if (b[section]) {
        b[section].push(s);
      } else {
        b[section] = [s];
      }
    });
    return b;
  };

  const getSortedSectionList = (sf: any) => {
    if (!containsBread) {
      const sections = Object.keys(sf);
      for (const s of sections) {
        const fields = sf[s];
        sf[s] = fields?.sort((a: any, b: any) => a?.default_details?.order - b?.default_details?.order);
      }
      return sf;
    } else {
      const sections = Object.keys(sf);
      for (const s of sections) {
        const fields = sf[s];

        const priorityFields = ['estimated_purchase_amount', 'first_name', 'middle_initial', 'last_name'];

        sf[s] = fields?.sort((a: any, b: any) => {
          const aId = a?.id;
          const bId = b?.id;

          if (priorityFields.includes(aId) && priorityFields.includes(bId)) {
            return priorityFields.indexOf(aId) - priorityFields.indexOf(bId);
          }

          if (priorityFields.includes(aId)) {
            return -1;
          }

          if (priorityFields.includes(bId)) {
            return 1;
          }

          return a?.default_details?.order - b?.default_details?.order;
        });
      }
      return sf;
    }
  };

  const showEmpty = (key: any) => <React.Fragment key={`${key}-empty`}></React.Fragment>;

  const showField = (id: any, objSchema: any, section: string) => {
    if (FieldComponent) {
      return FieldComponent(id, formProps, objSchema, formikProps);
    } else {
      return (
        <DynamicField
          key={`dynamic_field_${id}`}
          formProps={formProps}
          objSchema={objSchema}
          id={id}
          containsBread={containsBread}
          required_fields={required_fields}
          // modified line
          registerFieldRef={registerFieldRef}
          handleFieldBlur={handleFieldBlur}
          section={section}
        // end modified
        />
      );
    }
  };

  const showBasedOnCondition = (field: any, formProps: FormRenderProps, id: any) => {
    const { condition } = field[fieldDetailType];
    if (condition && !isEmpty(condition)) {
      const selectedFieldObj: RootFieldDetailsI = JSON.parse(JSON.stringify(field[fieldDetailType]));
      const conditions: FieldConditionsI = selectedFieldObj.condition;
      const actionType: FieldConditionActionTypes = conditions.type;
      const ruleIds = conditions.rules.map((r: any) => r.id);
      const formValues = formProps.values;
      const fieldValues = ruleIds.map((ruleId) => formValues[ruleId]);
      const rule = validateRules(selectedFieldObj.condition, ruleIds, fieldValues);
      const section = field.section;
      const ActionTypesToShow = [
        FieldConditionActionTypes.show,
        FieldConditionActionTypes.require,
        FieldConditionActionTypes.optional,
      ];

      if (rule && ActionTypesToShow.includes(actionType)) {
        if (actionType === FieldConditionActionTypes.require) {
          selectedFieldObj.required = true;
          return showField(id, selectedFieldObj, section);
        } else {
          return showField(id, selectedFieldObj, section);
        }
        return;
      } else {
        return showEmpty(id);
      }
    } else {
      const objSchema = field[fieldDetailType];
      const section = field.section;

      return showField(id, objSchema, section);
    }
  };

  const sectionList = getSectionList();
  const sortedSectionList = getSortedSectionList(sectionList);

  const sortSections = (s: any) => {
    const sectionOrder = [
      'basicFormFields',
      'patientFields',
      'identificationFields',
      'housingFields',
      'employmentFields',
      'purchaseFields',
      'bankingFields',
      'coApplicantFields',
    ];
    const updated: any[] = [];
    sectionOrder.forEach((section) => {
      if (s.includes(section)) {
        updated.push(section);
      }
    });
    return updated;
  };

  const sections = Object.keys(sectionList);
  const sortedSections = sortSections(sections);

  return (
    <>
      {sortedSections.map((section, index) => {
        if (section === 'coApplicantFields') return null;
        // if industry doesn't exist (it should), we just check for 0 which won't show the fields
        if (section === 'patientFields' && !MEDICAL_INDUSTRY_IDS.includes(industry_id ?? 0)) return null;

        const renderedFields = sortedSectionList[section].map((f: any) => {
          if (f?.section === 'coApplicantFields') {
            return null;
          }
          const { id } = f;
          return showBasedOnCondition(f, formProps, id);
        });

        const hasVisibleFields = renderedFields.some((field: React.ReactElement | null) => {
          if (!field) return false;
          if (field.type !== React.Fragment) return true;
          return field.props?.children !== undefined && field.props?.children !== null;
        });

        if (!hasVisibleFields) return null;

        return (
          <React.Fragment key={`${section}-sections-${index}`}>
            <Grid item xs={12} style={{ textAlign: 'left', position: 'relative' }} mt={2}>
              <Typography sx={{ background: 'white', textTransform: 'uppercase', color: '#243F8F' }} variant="h6">
                {convertSectionToLabel(section)}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                // mx: { xs: 0, md: 5 },
                // borderTop: '1px solid #000',
                // borderColor: '#243F8F66',
                padding: 3,
              }}
            >
              <Grid container direction={'row'} spacing={3} style={{ marginTop: 2 }}>
                {renderedFields}
              </Grid>
            </Grid>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default DynamicSections;
